import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import theme from "assets/theme";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";
import themeRTL from "assets/theme/theme-rtl";
import Sidenav from "examples/Sidenav";
import { useEffect, useMemo, useState } from "react";
import { Toaster } from "react-hot-toast";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { Box, LinearProgress, TextField, Typography } from "@mui/material";
import {
  default as brandDark,
  default as brandWhite,
} from "assets/images/main-logo.png";
import ForgotPassword from "auth/forgot-password";
import Login from "auth/login";
import ResetPassword from "auth/reset-password";
import axios from "axios";
import AccessDenied from "components/AccessDenied";
import LoadingBar from "components/Loading/loading-bar";
import LoadingScreenBlocker from "components/Loading/loading-screen-blocker";
import { ACCOUNT_TYPE } from "const";
import { REQUIRE_OTP } from "config";
import {
  setMiniSidenav,
  useAuth,
  useMaterialUIController,
} from "contexts/auth.context";
import { useLanguage } from "contexts/language.context";
import { useLoading } from "contexts/loading.context";
import ProtectedRoute from "examples/ProtectedRoute";
import { SetupOTPDialog } from "layouts/profile/components/PlatformSettings";
import Reset from "layouts/reset";
import filteredRoutes from "services/filter-routes";
import rolesService from "services/roles-service";
import rtlPlugin from "stylis-plugin-rtl";
import HttpService from "services/htttp.service";
import { setupAxiosInterceptors } from "./services/interceptor";
import DevelopmentFeature from "./components/DevelopmentFeature";
import { usePermissionMonitor } from "./components/PermissionMonitor";

const routeExceptions = {
  80: [
    "sheets",
    "sheet-templates",
    "logsheet-entities",
    "sheet-entity-elements",
    "sheet-template-categories",
    "sheet-properties",
    "units",
  ],
};

export default function App() {
  const authContext = useAuth();
  const navigate = useNavigate();
  const { label, labels } = useLanguage();
  const { startLoading, stopLoading, resetLoading } = useLoading();
  const [routes, setRoutes] = useState(null);
  const [interceptorsReady, setInterceptorsReady] = useState(false);

  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    function onlineHandler() {
      setIsOnline(true);
    }

    function offlineHandler() {
      setIsOnline(false);
    }

    window.addEventListener("online", onlineHandler);
    window.addEventListener("offline", offlineHandler);

    return () => {
      window.removeEventListener("online", onlineHandler);
      window.removeEventListener("offline", offlineHandler);
    };
  }, []);

  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const globalLoaderEnabled = authContext.user?.account?.accountTypeId === ACCOUNT_TYPE.TECHNICAL_ADMINISTRATOR;
    //authContext.user?.account?.accountTypeId === ACCOUNT_TYPE.TECHNICAL_ADMINISTRATOR ||
    //authContext.user?.account?.accountTypeId === ACCOUNT_TYPE.ADMINISTRATOR ||

  useEffect(() => {
    if (!globalLoaderEnabled) {
      resetLoading();
    }
    HttpService.setGlobalLoadingHandlers({
      startLoading,
      stopLoading,
      enabled: globalLoaderEnabled,
    });

    const cleanupInterceptors = setupAxiosInterceptors({
      onUnauthenticated: () => {
        if (String(window.location.href).includes("/auth/")) {
          return;
        }
        authContext.logout(true);
      },
      onUnauthorized: () => {},
    });
    setInterceptorsReady(true);

    return () => {
      cleanupInterceptors();
      HttpService.setGlobalLoadingHandlers({ enabled: false });
    };
  }, [globalLoaderEnabled, resetLoading, startLoading, stopLoading]);

  useEffect(() => {
    if (!interceptorsReady) {
      return;
    }

    async function asyncHelper(cancelToken) {
      if (!authContext.isAuthenticated) {
        setRoutes([]);
        return;
      }

      authContext.setPermissions(null);
      const [response, data] = await rolesService.getMyPermissions();
      authContext.setPermissions(response.ok ? data : []);
      const nextRoutes = await filteredRoutes(
        label,
        cancelToken,
        authContext.logout,
        authContext.isTechnicalAdministrator,
        routeExceptions,
        authContext.user?.id,
        response.ok ? data : [],
      );
      setRoutes(nextRoutes || []);
    }
    const source = axios.CancelToken.source();
    asyncHelper(source.token);
    return () => {
      source.cancel("Component unmounted");
    };
  }, [
    interceptorsReady,
    authContext.isAuthenticated,
    authContext.user?.role?.id,
    labels,
    authContext.user?.isTechnicalAdministrator,
  ]);

  useEffect(() => {
    if (routes && routes?.length && authContext.lastOpenPath) {
      authContext.setLastOpenPath("");
      navigate(authContext.lastOpenPath);
    }
  }, [routes]);

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.components) {
        return getRoutes(route.components);
      }

      if (route.route && route.type !== "auth") {
        return (
          <Route
            path={route.route + (route.includesInternalUrls ? "/*" : "")}
            element={
              <ProtectedRoute isAuthenticated={authContext.isAuthenticated}>
                {route.component}
              </ProtectedRoute>
            }
            key={route.key}
          />
        );
      }
      return null;
    });

  const waitingForProtectedRoutes =
    interceptorsReady &&
    authContext.isAuthenticated &&
    (!authContext.permissionsLoaded || routes === null);

  return (
    <>
      {isOnline &&
        (direction === "rtl" ? (
          <CacheProvider value={rtlCache}>
            <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
              <CssBaseline />
              <LoadingBar />
              <LoadingScreenBlocker />
              <LinearProgress
                sx={{
                  position: "fixed",
                  top: 0,
                  width: "calc(100vw - 17px)",
                  height: authContext.loading ? "5px" : 0,
                  left: 0,
                  transition: "all 0.8s ease-in-out",
                }}
                color="info"
              />
              {REQUIRE_OTP &&
              authContext.user &&
              (!authContext.user.isEnabledTFA ||
                !authContext.user.isVerifiedTFA) ? (
                <SetupOTPDialog open={true} setOpen={() => {}} />
              ) : waitingForProtectedRoutes ? (
                <Box
                  sx={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 3,
                  }}
                >
                  <LinearProgress sx={{ width: "240px" }} color="info" />
                </Box>
              ) : (
                <>
                  {layout === "dashboard" &&
                  authContext.isAuthenticated &&
                  routes?.length ? (
                    <Sidenav
                      color={sidenavColor}
                      brand={
                        (transparentSidenav && !darkMode) || whiteSidenav
                          ? brandDark
                          : brandWhite
                      }
                      brandName="IFPC360"
                      routes={routes}
                      onMouseEnter={handleOnMouseEnter}
                      onMouseLeave={handleOnMouseLeave}
                    />
                  ) : null}
                  <AllRoutes>{routes ? getRoutes(routes) : null}</AllRoutes>
                </>
              )}
            </ThemeProvider>
          </CacheProvider>
        ) : (
          <ThemeProvider theme={darkMode ? themeDark : theme}>
            <CssBaseline />
            <LoadingBar />
            <LoadingScreenBlocker />
            {REQUIRE_OTP &&
            authContext.user &&
            (!authContext.user.isEnabledTFA ||
              !authContext.user.isVerifiedTFA) ? (
              <SetupOTPDialog open={true} setOpen={() => {}} />
            ) : waitingForProtectedRoutes ? (
              <Box
                sx={{
                  minHeight: "100vh",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 3,
                }}
              >
                <LinearProgress sx={{ width: "240px" }} color="info" />
              </Box>
            ) : (
              <>
                {layout === "dashboard" &&
                authContext.isAuthenticated &&
                routes?.length ? (
                  <Sidenav
                    color={sidenavColor}
                    brand={
                      (transparentSidenav && !darkMode) || whiteSidenav
                        ? brandDark
                        : brandWhite
                    }
                    brandName="IFPC360"
                    routes={routes}
                    onMouseEnter={handleOnMouseEnter}
                    onMouseLeave={handleOnMouseLeave}
                  />
                ) : null}
                <AllRoutes>{routes ? getRoutes(routes) : null}</AllRoutes>
              </>
            )}
          </ThemeProvider>
        ))}

      <Toaster
        position="bottom-right"
        toastOptions={{
          success: {
            duration: 5000,
            style: { fontFamily: "Roboto" },
            iconTheme: {
              primary: "#48c2c3",
              secondary: "white",
            },
          },
          error: {
            duration: 20000,
            style: { fontFamily: "Roboto", fontSize: "1rem" },
            iconTheme: {
              primary: "#dc1b47",
              secondary: "white",
            },
          },
        }}
      />

      {!isOnline && "You are not connected to the internet."}

      <DevelopmentFeature dev>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            zIndex: 10000,
          }}
        >
          <Typography
            color="error"
            variant="h6"
            sx={{
              margin: "0 !important",
              padding: "0 !important",
            }}
          >
            DEVELOPMENT
          </Typography>
        </Box>
      </DevelopmentFeature>
    </>
  );
}

function AllRoutes({ children }) {
  const authContext = useAuth();

  return (
    <Routes>
      <Route path="reset" element={<Reset />} />
      <Route path="/access-denied" element={<AccessDenied type="page" />} />
      <Route
        path="/auth/reset-password/:token/:email"
        element={<ResetPassword />}
      />
      <Route path="/auth/login" element={<Login />} />

      <Route path="/auth/forgot-password" element={<ForgotPassword />} />

      {children}

      <Route
        path="*"
        element={
          <Navigate
            to={authContext.isAuthenticated ? "/audits" : "/auth/login"}
          />
        }
      />
    </Routes>
  );
}
